import { useState } from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { updateLogEntry, BuildLogEntry } from '../services/firebase';

interface EditEntryModalProps {
  entry: BuildLogEntry;
  onClose: () => void;
  onSaved: () => void;
}

export function EditEntryModal({ entry, onClose, onSaved }: EditEntryModalProps) {
  // Main Content State
  const [title, setTitle] = useState(entry.title);
  const [content, setContent] = useState(entry.content);
  
  // Flywheel Fields State (Mapped from your BuildLogEntry interface)
  const [problemSolved, setProblemSolved] = useState(entry.problemSolved || '');
  const [outcome, setOutcome] = useState(entry.outcome || '');
  
  // UI State
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      // Calling the existing function from your firebase.ts
      await updateLogEntry(entry.id, {
        title: title.trim(),
        content: content.trim(),
        problemSolved: problemSolved.trim(),
        outcome: outcome.trim(),
      });
      onSaved();
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#000000]/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden max-h-[90vh]">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
          <h2 className="text-xl font-bold font-display text-[#000000]">Edit Log Entry</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-[#000000] transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Title input */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Title</label>
            <input 
              value={title} 
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:border-[#000000] outline-none font-medium"
            />
          </div>

          {/* Flywheel Fields (Critical for Claude's Context) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Problem Solved</label>
              <input 
                value={problemSolved} 
                onChange={(e) => setProblemSolved(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                placeholder="What was broken?"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Outcome</label>
              <input 
                value={outcome} 
                onChange={(e) => setOutcome(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                placeholder="What is the result?"
              />
            </div>
          </div>

          {/* Write / Preview Tabs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100">
              <div className="flex gap-4">
                <button 
                  onClick={() => setIsPreview(false)}
                  className={`pb-2 text-sm font-bold border-b-2 transition-all ${!isPreview ? 'border-[#000000] text-[#000000]' : 'border-transparent text-[#6C6864]'}`}
                >
                  Write
                </button>
                <button 
                  onClick={() => setIsPreview(true)}
                  className={`pb-2 text-sm font-bold border-b-2 transition-all ${isPreview ? 'border-[#000000] text-[#000000]' : 'border-transparent text-[#6C6864]'}`}
                >
                  Preview
                </button>
              </div>
            </div>

            <div className="min-h-[300px]">
              {!isPreview ? (
                <textarea 
                  value={content} 
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full h-[350px] p-4 font-mono text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none resize-none"
                  placeholder="Detail your ship here..."
                />
              ) : (
                <div className="h-[350px] p-4 overflow-y-auto prose prose-slate max-w-none border border-transparent">
                  <ReactMarkdown>{content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-[#6C6864]">Cancel</button>
          <button 
            onClick={handleUpdate}
            disabled={isSaving}
            className="flex items-center gap-2 px-8 py-2 bg-[#000000] text-white rounded-lg font-bold hover:bg-[#333333] transition-all disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}